import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { MdDelete, MdEdit } from "react-icons/md";
import { CameraIcon, TrashIcon } from "@heroicons/react/24/outline";
import { useBannerHook } from "./useHook";
import Loader, { ImageWithFallback } from "../../Utils/loader";

function BannerManagement() {
  const { getBanners, addBanner, updateBanner, deleteBanner } = useBannerHook();

  const [banners, setBanners] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mainLoading, setMainLoading] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    file: null,
    title: "",
    description: "",
    link: "",
    status: true,
  });

  // Check if all required fields are filled
  const isFormValid = () => {
    return formData.title.trim() !== "" &&
      formData.file !== null &&
      formData.link.trim() !== "";
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      setMainLoading(true);
      await getBanners(setBanners);
    } catch (error) {
      console.error("Error fetching banners:", error);
    } finally {
      setMainLoading(false);
    }
  };

  const openModal = (index = null) => {
    if (index !== null && banners[index]) {
      const bannerToEdit = banners[index];
      setFormData({
        file: null,
        title: bannerToEdit.title,
        description: bannerToEdit.description,
        link: bannerToEdit.link,
        status: bannerToEdit.status,
      });
      setEditIndex(index);
      setImagePreview(bannerToEdit.image); // Show existing image preview
    } else {
      setFormData({
        file: null,
        title: "",
        description: "",
        link: "",
        status: true,
      });
      setEditIndex(null);
      setImagePreview(null);
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditIndex(null);
    setImagePreview(null);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, file });
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, file: null });
    setImagePreview(null);
  };

  const handleAddBanner = async () => {
    if (!isFormValid()) {
      Swal.fire(
        "Error!",
        "Please fill in all required fields (Title, Image, and Link).",
        "error"
      );
      return;
    }
    try {
      setLoading(true);
      const fd = new FormData();
      fd.append("image", formData.file);
      fd.append("title", formData.title);
      fd.append("description", formData.description);
      fd.append("link", formData.link);
      fd.append("status", formData.status);

      await addBanner(fd);
      Swal.fire("Success!", "Banner added successfully.", "success");
      await fetchBanners();
      closeModal();
    } catch (error) {
      Swal.fire("Error!", error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBanner = async () => {
    if (!formData.title.trim() || !formData.link.trim()) {
      Swal.fire("Error!", "Please fill in all required fields (Title and Link).", "error");
      return;
    }
    try {
      const bannerId = banners[editIndex].id;
      const fd = new FormData();
      if (formData.file) {
        fd.append("image", formData.file);
      }
      fd.append("title", formData.title);
      fd.append("description", formData.description);
      fd.append("link", formData.link);
      fd.append("status", formData.status);

      await updateBanner(bannerId, fd);
      Swal.fire("Success!", "Banner updated successfully.", "success");
      await fetchBanners();
      closeModal();
    } catch (error) {
      Swal.fire("Error!", error.message, "error");
    }
  };

  const handleDeleteBanner = (index) => {
    const bannerId = banners[index].id;
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteBanner(bannerId);
          Swal.fire("Deleted!", "Banner has been deleted.", "success");
          await fetchBanners();
        } catch (error) {
          Swal.fire("Error!", error.message, "error");
        }
      }
    });
  };

  return (
    <div className="bg-gray-100 p-4 min-h-screen">
      <h2 className="text-2xl font-bold mb-6 text-center">Manage Banners</h2>

      {mainLoading && (
        <Loader message="Loading banners..." />
      )}

      {!mainLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {banners?.map((banner, index) => {
            const bannerId = banner.id || index;

            return (
              <div
                key={bannerId}
                className="bg-white rounded-lg shadow overflow-hidden"
              >
                <div className="h-48 w-full">
                  <ImageWithFallback
                    src={banner.image}
                    alt={banner.title}
                    className="h-48 w-full object-cover"
                  />
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold">{banner.title}</h3>
                  <p className="text-gray-600 mt-2">{banner.description}</p>
                  <a
                    className="text-blue-500 text-sm mt-2 inline-block"
                    href={banner.link}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Visit Link
                  </a>
                  <div className="flex justify-end mt-4 space-x-2">
                    <button
                      className="flex items-center px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                      onClick={() => openModal(index)}
                    >
                      <MdEdit className="mr-1" /> Edit
                    </button>
                    <button
                      className="flex items-center px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                      onClick={() => handleDeleteBanner(index)}
                    >
                      <MdDelete className="mr-1" /> Delete
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!mainLoading && banners?.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No banners yet</h3>
          <p className="text-gray-500 mb-6">Get started by adding your first banner</p>
        </div>
      )}

      <div className="mt-6 text-center">
        <button
          className="w-full sm:w-auto bg-green-500 text-white px-6 py-3 rounded shadow hover:bg-green-600"
          onClick={() => openModal(null)}
        >
          Add Banner
        </button>
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              {editIndex !== null ? "Edit Banner" : "Add Banner"}
            </h2>

            {/* Image Upload Section */}
            <div className="space-y-4 mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Banner Image <span className="text-red-500">*</span>
              </label>

              {imagePreview ? (
                <div className="relative">
                  <div className="w-full h-48 bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                    <img
                      src={imagePreview}
                      alt="Banner preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white p-2 rounded-full transition-colors"
                    title="Remove image"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ) : (
                <label
                  htmlFor="banner-image-upload"
                  className="cursor-pointer w-full h-48 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center hover:bg-gray-100 transition-colors"
                >
                  <CameraIcon className="h-12 w-12 text-gray-400 mb-3" />
                  <span className="text-gray-600 font-medium">Upload Banner Image</span>
                  <span className="text-gray-400 text-sm mt-1">Click to browse files</span>
                </label>
              )}

              <input
                id="banner-image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
              />

              <p className="text-gray-500 text-xs">
                Image is required for banner creation
              </p>
            </div>

            <label className="block mb-2 font-semibold">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              className="w-full mb-4 p-2 border rounded"
              placeholder="Enter banner title"
            />

            <label className="block mb-2 font-semibold">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="w-full mb-4 p-2 border rounded"
              placeholder="Enter banner description"
            />

            <label className="block mb-2 font-semibold">
              Link <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.link}
              onChange={(e) =>
                setFormData({ ...formData, link: e.target.value })
              }
              className="w-full mb-4 p-2 border rounded"
              placeholder="Enter link for the banner"
            />

            <div className="flex justify-end">
              <button
                className="px-4 py-2 bg-gray-300 rounded mr-2"
                onClick={closeModal}
              >
                Cancel
              </button>
              {editIndex !== null ? (
                <button
                  className="px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleUpdateBanner}
                  disabled={!formData.title.trim() || !formData.link.trim()}
                >
                  Update
                </button>
              ) : (
                <button
                  className="px-4 py-2 bg-green-500 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleAddBanner}
                  disabled={loading || !isFormValid()}
                >
                  {loading ? "Adding..." : "Add"}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BannerManagement;
